import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";

import WithAppLayout from "../../../components/WithAppLayout";

import { GET_CANDIDATES_QUERY } from "../../../graphql/agencies";
import { EDIT_CANDIDATE_PATH } from "../../../routes";
import { LoadingIndicator } from "../../../components/LoadingIndicator";
import __ from "../../../helpers/translations";
import { getLocalStorageItem } from "../../../helpers/utils";
import {
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  HStack,
  useToast,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { FiSearch } from "react-icons/fi";
import PaginationTable from "../../../components/PaginationTable/PaginationTable";
import { COLUMN_PRIORITY } from "../../../components/PageTableWithSearch/tableDataHelper";
import { createColumnHelper } from "@tanstack/react-table";
import { GET_ALL_CANDIDATE_ROLES_AND_SKILLS_QUERY } from "../../../graphql/jobs_admin";
import FavouriteIcon from "../../../components/common/FavouriteIcon";
import FavouritesOnly from "../../../components/common/FavouritesOnly";
import { AgencyOnFavourite } from "../../../components/Shortlist/AgencyShortlistActions";
const columnHelper = createColumnHelper();

function AllCandidatesPageInner({
  currentUser,
  loading,
}) {
  const LS_KEY = "agency-candidates-filters";
  const LS_PAGE_KEY = "agency-candidates-pagination";
  const localStorage = window.localStorage;
  const [roleOptions, setRoleOptions] = useState([]);
  const [skillOptions, setSkillOptions] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);

  const defaultFilters = getLocalStorageItem(LS_KEY, {
    search: "",
    favourite: false,
    roles: [],
    skills: [],
  });
  const [filters, setFilters] = useState(defaultFilters);

  const defaultPagination = getLocalStorageItem(LS_PAGE_KEY, { pageIndex: 0, pageSize: 10 });
  const [pagination, setPagination] = useState(defaultPagination);
  const setFiltersAndSave = useCallback((patchFilters) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters, ...patchFilters };
      window.localStorage.setItem(LS_KEY, JSON.stringify(newFilters));
      return newFilters;
    });
  }, []);
  const savePagination = (data) => {
    data && localStorage.setItem(LS_PAGE_KEY, JSON.stringify(data));
  };
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search); // New state for debounced search
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const handler = setTimeout(() => {
      setFiltersAndSave({ search: debouncedSearch });
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [debouncedSearch, setFiltersAndSave]);

  const { loading: rolesLoading } = useQuery(GET_ALL_CANDIDATE_ROLES_AND_SKILLS_QUERY, {
    onCompleted: (data) => {
      setRoleOptions(data.candidateRoles.map((role) => ({ label: role.name, value: role.id })));
      const structuredRoles = filters?.roles.map((roleId) => {
        const role = data.candidateRoles.find((r) => r.id === roleId);
        return role ? { label: role.name, value: role.id } : null;
      }).filter(Boolean);
      setSelectedRoles(structuredRoles);
      setSkillOptions(data.allSkills.map((skill) => ({ label: skill.name, value: skill.id })));
      const structuredSkills = filters?.skills.map((skillId) => {
        const skill = data.allSkills.find((s) => s.id === skillId);
        return skill ? { label: skill.name, value: skill.id } : null;
      }).filter(Boolean);
      setSelectedSkills(structuredSkills);
    },
  });

  if (loading || rolesLoading) {
    return <LoadingIndicator />;
  }

  const MultipleItemCell = ({ items, limit = 0 }) => {
    if (!items) {
      return null;
    }
    const limitedItems = limit > 0 ? items.slice(0, limit) : items;
    const remainingItems = limit > 0 ? items.slice(limit) : [];
    if (remainingItems.length > 0) {
      limitedItems.push(`+${remainingItems.length}`);
    }
    return (
      <HStack gap="2px">
        {limitedItems.map((item, idx) => (
          <Text
            key={idx}
            fontSize="sm"
            color="gray.50"
            p="4px 8px"
            bg="whiteAlpha.300"
            borderRadius="6px"
          >
            {item}
          </Text>
        ))}
      </HStack>
    );
  };
  const NameCell = ({ row }) => {
    return (
      <Stack gap={0}>
        <Text fontSize="sm" color="white">
          {row.original.fullName}
        </Text>
        <Text fontSize="sm" color="whiteAlpha.800">
          {row.original.email}
        </Text>
      </Stack>
    )
  };
  const candidatesColumns = [
    columnHelper.accessor("favourite", {
      cell: (info) => {
        const candidateReport = {
          favourite: info.getValue(),
          candidate: info.row.original
        };
        return (
          <FavouriteIcon favourite={info.getValue()} fontSize="24px"
            onClick={e => {
              e.stopPropagation();
              AgencyOnFavourite({
                toast: toast,
                candidateReport,
                refetch: () => {
                  setFilters((prevFilters) => ({ ...prevFilters })); // Trigger a refetch by updating filters
                }
              });
            }}
          />
        );
      },
      header: "",
      // The star is how recruiters favourite a candidate, so it never
      // collapses and is not offered in the Columns menu.
      enableHiding: false,
      meta: {
        priority: COLUMN_PRIORITY.IDENTIFYING,
        styles: {
          padding: "0 0 0 24px",
        }
      }
    }),
    columnHelper.accessor("name", {
      cell: (info) => NameCell({ row: info.row }),
      header: "Name",
      meta: { priority: COLUMN_PRIORITY.IDENTIFYING },
    }),
    columnHelper.accessor(
      (item) =>
        item?.roles || [],
      {
        // Accessor functions need an explicit id for the responsive
        // collapsing and the Columns menu to address the column.
        id: "roles",
        cell: (info) => (<MultipleItemCell items={info.getValue()} limit={2} />),
        header: "Roles",
        meta: { priority: COLUMN_PRIORITY.IMPORTANT },
      }
    ),
    columnHelper.accessor(
      (item) =>
        item?.skills || [],
      {
        id: "skills",
        cell: (info) => (<MultipleItemCell items={info.getValue()} limit={3} />),
        header: "Skills",
        meta: { priority: COLUMN_PRIORITY.SECONDARY },
      }
    ),
    columnHelper.accessor(
      (item) =>
        !!item?.hasResume,
      {
        id: "hasResume",
        cell: (info) => (info.getValue() ? "Yes" : "No"),
        header: "CV/Resume",
        meta: { priority: COLUMN_PRIORITY.SECONDARY },
      }
    ),
    columnHelper.accessor("candidateProfileId", {
      cell: (info) => {
        const id = info.getValue();
        if (!id) return "";
        return id.length > 20 ? `${id.slice(0, 20)}...` : id;
      },
      header: "ID",
      meta: { priority: COLUMN_PRIORITY.OPTIONAL },
    }),
  ];

  const filterInputs = (
    <HStack spacing={4} alignItems="center">
      <HStack>
        <InputGroup maxW="xs">
          <Input
            placeholder="Search"
            value={debouncedSearch}
            onChange={(e) => setDebouncedSearch(e.target.value)}
            variant="black"
          />
          <InputRightElement pointerEvents="none">
            <Icon as={FiSearch} color="white" boxSize="5" />
          </InputRightElement>
        </InputGroup>
      </HStack>
      <FavouritesOnly
        isChecked={filters.favourite}
        onChange={(e) => {
          setFiltersAndSave({ favourite: e.target.checked });
        }}
        size="lg"
      />
      <HStack spacing={4} ml={"auto"}>
        <Select
          isMulti={true}
          defaultValue={selectedRoles}
          options={roleOptions}
          onChange={(selected) => {
            const selectedValues = selected.map((option) => option.value);
            setFiltersAndSave({ roles: selectedValues });
          }}
          placeholder="Select Role"
          chakraStyles={{
            container: (provided) => ({
              ...provided,
              minWidth: "300px"
            })
          }}
        />
        <Select
          isMulti={true}
          defaultValue={selectedSkills}
          options={skillOptions}
          onChange={(selected) => {
            const selectedValues = selected.map((option) => option.value);
            setFiltersAndSave({ skills: selectedValues });
          }}
          placeholder="Select Skill"
          chakraStyles={{
            container: (provided) => ({
              ...provided,
              minWidth: "300px"
            })
          }}
        />
      </HStack>
    </HStack>
  );

  return (
    <PaginationTable
      title={__("Workers", currentUser?.agency?.type)}
      tableId="agency-candidates"
      columns={candidatesColumns}
      query={GET_CANDIDATES_QUERY}
      queryName="agencyCandidates"
      itemKey="candidates"
      filters={filters}
      paginationState={[pagination, setPagination, savePagination]}
      filterInputs={filterInputs}
      onClickRow={(candidate) => {
        navigate(EDIT_CANDIDATE_PATH.replace(":id", candidate.candidateProfileId));
      }}
    >
    </PaginationTable>
  );
}

export default WithAppLayout(AllCandidatesPageInner);
